/**
 * The one-word verdict a captured leg deserves in a list row.
 *
 * A history list is read at a glance, and the question it has to answer before
 * anything else is "which of these went wrong". The verdict is deliberately
 * blunt: `referred` is not success, and a rolled-back merge is a failure even
 * though every individual REFER may have been accepted.
 */

import type { DevToolsBadgeVariant } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsStatusBadge/index.types';
import type { CallDebugInfo } from 'Modules/softphone/services/DebugService';

export interface LegHealth {
  label: string;
  variant: DevToolsBadgeVariant;
}

/** Peer connection states that mean the media path never came up, or dropped. */
const BROKEN_CONNECTION_STATES = ['failed', 'disconnected', 'closed'];

export const getLegHealth = (info: CallDebugInfo): LegHealth => {
  if (info.conference) {
    if (info.conference.rolledBack) {
      return { label: 'rolled back', variant: 'error' };
    }

    return info.scenario === 'conference-room'
      ? { label: 'room', variant: 'info' }
      : { label: 'merged', variant: 'success' };
  }

  if (info.transfer) {
    switch (info.transfer.outcome) {
      case 'accepted':
        return { label: 'accepted', variant: 'success' };
      case 'failed':
        return { label: 'failed', variant: 'error' };
      case 'cancelled':
        return { label: 'cancelled', variant: 'neutral' };
      default:
        // `referred` only means the REFER left the client.
        return { label: 'referred', variant: 'warning' };
    }
  }

  if (
    info.iceConnectionState &&
    BROKEN_CONNECTION_STATES.includes(info.iceConnectionState)
  ) {
    return { label: 'ice failed', variant: 'error' };
  }

  // No remote candidate is the single most common cause of one-way audio, and
  // it survives a call that otherwise looks perfectly healthy.
  if (info.iceCandidates && info.iceCandidates.remote.length === 0) {
    return { label: 'no remote ice', variant: 'warning' };
  }

  return { label: 'clean', variant: 'success' };
};
